import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { userAppContext } from '../../context/Context';
import fetchEndpoint from '../../services/fetchEndpoint';
import GeneGeneralInfo from './GeneGeneralInfo';
import GeneSynonyms from './GeneSynonyms';
import CollapsibleSection from '../QueriedContent/CollapsibleSection';
import Loader from '../Common/Loader';
import { useMappingStackStore } from '../../store/mappingStackStore';
import { MAPPING_URL_PARAM, encodeMappingStateForUrl } from '../../utils/mappingUrlState';

interface GeneInfoPanelContentProps {
  geneId: string;
  geneSymbol: string;
  onClose: () => void;
}

export default function GeneInfoPanelContent({ geneId, geneSymbol, onClose }: GeneInfoPanelContentProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { authorization, isAuthenticated, setIsAuthenticated } = userAppContext();
  const [geneDetailInfo, setGeneDetailInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const mappingState = useMappingStackStore.getState();
  const serializableStackSnapshot = mappingState.stack.map(level => ({
    ...level,
    labelNode: undefined,
  }));
  const fromSearchParams = new URLSearchParams(location.search);
  if (mappingState.stack.length > 1 && mappingState.currentIndex > 0) {
    fromSearchParams.set(
      MAPPING_URL_PARAM,
      encodeMappingStateForUrl({
        stack: serializableStackSnapshot,
        currentIndex: mappingState.currentIndex,
        tableStateByLevelId: mappingState.tableStateByLevelId,
      }),
    );
  }

  useEffect(() => {
    if (!geneId) return;
    setGeneDetailInfo(null);
    setError(false);
    setLoading(true);

    const unauth = isAuthenticated ? '' : '?skey=unauth';
    const endpoint = `api/gene/detail/${encodeURIComponent(geneId)}${unauth}`;
    fetchEndpoint(endpoint, authorization, setIsAuthenticated)
      .then(response => {
        if (response?.status === 'OK') {
          setGeneDetailInfo(response.payload);
        } else {
          setError(true);
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [geneId, isAuthenticated, authorization]);

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h2 className="text-sm font-semibold  truncate min-w-0 mb-0">{geneSymbol}</h2>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-200 flex-shrink-0"
          aria-label="Close"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {loading && (
          <div className="flex justify-center items-center h-24">
            <Loader isInnerSpinner />
          </div>
        )}

        {!loading && error && (
          <div className="p-4 text-sm text-red-600">Could not load gene information.</div>
        )}

        {!loading && !error && geneDetailInfo && (
          <div className="space-y-0">
            <CollapsibleSection title="General Info" defaultOpen>
              <div className="p-2">
                <GeneGeneralInfo geneDetailInfo={geneDetailInfo} linkMaxTcesGtp />
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Synonyms" defaultOpen={false}>
              <div className="p-2">
                <GeneSynonyms geneDetailInfo={geneDetailInfo} />
              </div>
            </CollapsibleSection>

            <div className="px-3 py-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/gene-pathways?symbol=${encodeURIComponent(geneDetailInfo.symbol ?? geneSymbol)}${geneDetailInfo.ncbiID != null ? `&id=${encodeURIComponent(geneDetailInfo.ncbiID)}` : ''}`,
                    {
                      state: {
                        from: {
                          pathname: location.pathname,
                          search: `?${fromSearchParams.toString()}`,
                          hash: location.hash,
                        },
                      },
                    },
                  )
                }
                className="flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 rounded-md px-2 py-1.5 transition-colors w-full"
              >
                <MapIcon className="w-4 h-4 flex-shrink-0" />
                View Pathways
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
